import loadable from "@loadable/component"
import React from "react"
import { Switch } from "react-router-dom"

import MerchantLoading from "components/merchant-loading"
import { jewlr_t, retry } from "helpers/application"
import { enableKlarnaCheckout } from "helpers/checkout/checkout_options"
import { to_currency } from "helpers/number"
import { Route } from "helpers/sentry"

import InfoRoute from "./route"

const loadableDefaultOptions = {
  fallback: <MerchantLoading />,
}

const GenericNotFound = loadable(
  () => retry(() => import("app/generic-not-found")),
  loadableDefaultOptions
)
const GiftCard = loadable(
  () => retry(() => import("areas/info/gift-card")),
  loadableDefaultOptions
)
const Hallmark = loadable(
  () => retry(() => import("areas/info/hallmark")),
  loadableDefaultOptions
)
const Klarna = loadable(
  () => retry(() => import("areas/info/klarna")),
  loadableDefaultOptions
)
const KlarnaAffirm = loadable(
  () => retry(() => import("areas/info/klarna_affirm")),
  loadableDefaultOptions
)
const ReturnPolicy = loadable(
  () => retry(() => import("areas/info/return-policy")),
  loadableDefaultOptions
)
const AccessibilityPlan = loadable(
  () => retry(() => import("../accessibility-plan")),
  loadableDefaultOptions
)
const AccessibilityStatement = loadable(
  () => retry(() => import("../accessibility-statement")),
  loadableDefaultOptions
)
const Affirm = loadable(
  () => retry(() => import("../affirm")),
  loadableDefaultOptions
)
const Reviews = loadable(
  () => retry(() => import("../reviews")),
  loadableDefaultOptions
)
const WarrantyInformation = loadable(
  () => retry(() => import("../warranty/index")),
  loadableDefaultOptions
)
const ChainsInformation = loadable(
  () => retry(() => import("./chains-information")),
  loadableDefaultOptions
)
const CorporateSales = loadable(
  () => retry(() => import("./corporate-sales")),
  loadableDefaultOptions
)
const Diamond = loadable(
  () => retry(() => import("./diamond")),
  loadableDefaultOptions
)
const DiamondCuration = loadable(
  () => retry(() => import("./diamond-curation")),
  loadableDefaultOptions
)
const Gemstones = loadable(
  () => retry(() => import("./gemstones")),
  loadableDefaultOptions
)
const HowItsMade = loadable(
  () => retry(() => import("./how-its-made")),
  loadableDefaultOptions
)
const JahnaviMala = loadable(
  () => retry(() => import("./jahnavi-mala-project")),
  loadableDefaultOptions
)
const Packaging = loadable(
  () => retry(() => import("./packaging")),
  loadableDefaultOptions
)
const Partnerships = loadable(
  () => retry(() => import("./partnerships")),
  loadableDefaultOptions
)
const Press = loadable(
  () => retry(() => import("./press")),
  loadableDefaultOptions
)
const DisruptiveRing = loadable(
  () => retry(() => import("./press/disruptive-ring")),
  loadableDefaultOptions
)
const MostInnovative = loadable(
  () => retry(() => import("./press/most-innovative")),
  loadableDefaultOptions
)
const PersonalTouch = loadable(
  () => retry(() => import("./press/personal-touch")),
  loadableDefaultOptions
)
const PurePlay = loadable(
  () => retry(() => import("./press/pureplay")),
  loadableDefaultOptions
)
const SketchFab = loadable(
  () => retry(() => import("./press/sketchfab")),
  loadableDefaultOptions
)
const PrivacyNotice = loadable(
  () => retry(() => import("./privacy-notice")),
  loadableDefaultOptions
)
const PrivacyPolicy = loadable(
  () => retry(() => import("./privacy-policy")),
  loadableDefaultOptions
)
const RingSizing = loadable(
  () => retry(() => import("./ring-sizing")),
  loadableDefaultOptions
)
const ShippingInformation = loadable(
  () => retry(() => import("./shipping-information")),
  loadableDefaultOptions
)
const TermsConditions = loadable(
  () => retry(() => import("./terms-conditions")),
  loadableDefaultOptions
)
const VipTermsConditions = loadable(
  () => retry(() => import("./vip-terms-conditions")),
  loadableDefaultOptions
)
const WarrantyOld = loadable(
  () => retry(() => import("./warranty/warranty-old")),
  loadableDefaultOptions
)
const WatchCare = loadable(
  () => retry(() => import("./watches/care")),
  loadableDefaultOptions
)
const WatchSizing = loadable(
  () => retry(() => import("./watches/sizing")),
  loadableDefaultOptions
)
const WhyChooseJewlr = loadable(
  () => retry(() => import("./why-choose-jewlr")),
  loadableDefaultOptions
)

const Info = () => {
  return (
    <Switch>
      <InfoRoute
        component={WhyChooseJewlr}
        layout="company"
        path="/info/why_choose_jewlr"
        showCta
        title="Why Choose Jewlr"
      />
      <InfoRoute
        component={Reviews}
        newLayout
        path="/reviews"
        showCta
        title="Customer Reviews"
      />
      <InfoRoute
        component={PurePlay}
        layout="press"
        newLayout
        path="/info/press/jewlr_wins_2018_pure_play_awards"
        title="Jewlr Wins 2018 Pure Play of the Year Award"
      />
      <InfoRoute
        component={SketchFab}
        layout="press"
        newLayout
        path="/info/press/sketchfab_disrupts_tranditional_adv"
        title="Sketchfab Disrupts Traditional Banner Advertising"
      />
      <InfoRoute
        component={PersonalTouch}
        layout="press"
        newLayout
        path="/info/press/tony_davis_praises_jewlr_personal_touch"
        title="Tony Davis Praises Jewlr.com's Personal Touch"
      />
      <InfoRoute
        component={DisruptiveRing}
        layout="press"
        newLayout
        path="/info/press/online_jeweller_has_a_disruptive_ring"
        title="Online Jeweller Business Has A Disruptive Ring"
      />
      <InfoRoute
        component={MostInnovative}
        layout="press"
        newLayout
        path="/info/press/jewlr_named_innovative_retailer_in_2013"
        title="Jewlr Wins 2013 Most Innovative Retailer Award"
      />
      <InfoRoute
        component={Press}
        exact
        layout="company"
        path="/info/press"
        title="Press"
      />
      <InfoRoute
        component={Partnerships}
        exact
        path="/info/partnerships"
        removeLayout
        title="Partnerships"
      />
      <InfoRoute
        component={TermsConditions}
        newLayout
        path="/info/terms-conditions"
        title="Terms and Conditions"
      />
      <InfoRoute
        component={PrivacyPolicy}
        newLayout
        path="/info/privacy-policy"
        title="Privacy Policy"
      />
      <InfoRoute
        component={AccessibilityStatement}
        hidePageTitle
        newLayout
        path="/info/accessibility-statement"
        title="Web Accessibility Statement"
      />
      <InfoRoute
        component={AccessibilityPlan}
        hidePageTitle
        newLayout
        path="/info/accessibility-plan"
        title="Web Accessibility Plan"
      />
      <InfoRoute
        component={PrivacyNotice}
        newLayout
        path="/info/privacy_notice"
        title="Privacy Notice"
      />
      <InfoRoute
        component={CorporateSales}
        newLayout
        path="/info/corporate_sales"
        showCta
        title="Corporate Sales"
      />
      <InfoRoute
        component={HowItsMade}
        layout="learn"
        path="/info/how_its_made"
        showCta
        title="How It's Made"
      />
      <InfoRoute
        component={Gemstones}
        path="/info/gemstones"
        showCta
        title="Gemstone Information"
      />
      <InfoRoute
        component={Packaging}
        isNewDesign
        newLayout
        path="/info/packaging"
        showCta
        title="Packaging"
      />
      <InfoRoute
        component={GiftCard}
        hidePageTitle
        newLayout
        path="/info/gift_cards"
        title="Jewlr Gift Cards"
      />
      <InfoRoute
        component={RingSizing}
        newLayout
        path="/info/ring_sizing"
        showCta
        title="Ring Sizing"
      />
      <InfoRoute
        component={ShippingInformation}
        path="/info/shipping_information"
        showCta
        title={`${jewlr_t("terms.Shipping")} Information`}
      />
      <InfoRoute
        component={ReturnPolicy}
        path="/info/returns"
        removeLayout
        title="Return Policy"
      />

      <InfoRoute
        component={VipTermsConditions}
        layout="company"
        path="/info/vip-terms-conditions"
        title="VIP Terms and Conditions"
      />

      {sl.config.enable_extend_warranty ? (
        <InfoRoute
          component={WarrantyInformation}
          path="/info/warranty-information"
          showCta
          title="Warranty & Protection"
        />
      ) : (
        <InfoRoute
          component={WarrantyOld}
          newLayout
          path="/info/warranty-information"
          showCta
          title="Warranty Information"
        />
      )}

      {enableKlarnaCheckout() && !sl.config.enable_affirm && (
        <InfoRoute
          component={Klarna}
          path="/info/payment-plans"
          title="Klarna"
        />
      )}
      {sl.config.enable_affirm && !enableKlarnaCheckout() && (
        <InfoRoute
          component={Affirm}
          path="/info/payment-plans"
          paymentPrices={{
            description: to_currency(250),
            simpleFinancing: to_currency(1264),
            simpleFinancingRate: to_currency(421.33, { precision: 2 }),
            splitPay: to_currency(200),
            splitPayRate: to_currency(50),
          }}
          showCta
          title="Affirm"
        />
      )}
      {sl.config.enable_affirm && enableKlarnaCheckout() && (
        <InfoRoute
          component={KlarnaAffirm}
          path="/info/payment-plans"
          paymentPrices={{
            affirmOrderMin: to_currency(249.99, { precision: 2 }),
            klarnaFinancingRate: to_currency(210.67, { precision: 2 }),
            klarnaMin: to_currency(35),
            klarnaOrderMin: to_currency(149),
            orderMin: to_currency(150),
            simpleFinancing: to_currency(1264),
            simpleFinancingRate: to_currency(421.33, { precision: 2 }),
            splitPay: to_currency(200),
            splitPayRate: to_currency(50),
          }}
          title="Payment Plans"
        />
      )}

      <InfoRoute
        component={ChainsInformation}
        newLayout
        path="/info/chains_information"
        showCta
        title="Chains Information"
      />
      <InfoRoute
        component={JahnaviMala}
        newLayout
        path="/info/jahnavi-mala-project"
        title="Jahnavi Mala Project"
      />
      <InfoRoute
        component={Diamond}
        path="/info/diamond"
        title="Diamonds & The 4 Cs"
      />
      <InfoRoute
        component={DiamondCuration}
        newLayout
        path="/info/diamond_curation"
        title="Diamond Curation"
      />
      <InfoRoute component={Hallmark} path="/info/hallmark" title="Hallmark" />
      {sl.config.enable_watches && (
        <>
          <InfoRoute
            component={WatchSizing}
            path="/info/watch-sizing"
            title="Watch Resizing"
          />
          <InfoRoute
            component={WatchCare}
            path="/info/watch-care"
            title="Watch Care"
          />
        </>
      )}
      <Route component={GenericNotFound} />
    </Switch>
  )
}
export default Info
